import { useEffect, useState, useCallback } from "react"
import { motion } from "framer-motion"
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  CalendarDays,
  CalendarRange,
  X,
} from "lucide-react"
import { getAppointments, updateAppointment, getCalendarRoutines } from "../../api/appointments"
import type { CalendarRoutine } from "../../api/appointments"
import type { Appointment } from "../../types"
import MonthView from "./appointments/MonthView"
import WeekView from "./appointments/WeekView"
import AddAppointmentModal from "./appointments/AddAppointmentModal"
import EditAppointmentModal from "./appointments/EditAppointmentModal"

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
]

type ViewMode = "month" | "week"

// Format date as YYYY-MM-DD using local timezone (avoids UTC shift from toISOString)
function localDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function loadPeriodData(y: number, m: number) {
  const startDate = localDateStr(new Date(y, m, 1))
  const endDate = localDateStr(new Date(y, m + 1, 0))
  return Promise.all([
    getAppointments({ start_date: startDate, end_date: endDate }),
    getCalendarRoutines({ start_date: startDate, end_date: endDate }),
  ])
}

export default function AppointmentsTab() {
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [routines, setRoutines] = useState<CalendarRoutine[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<ViewMode>("month")
  const [currentDate, setCurrentDate] = useState(new Date())
  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState<Appointment | null>(null)
  const [seriesMessage, setSeriesMessage] = useState<string | null>(null)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const periodKey = `${year}-${month}`
  const [activePeriod, setActivePeriod] = useState(periodKey)
  if (activePeriod !== periodKey) {
    setActivePeriod(periodKey)
    setLoading(true)
  }

  const fetchAppointments = useCallback(async () => {
    try {
      const [apptData, routineData] = await loadPeriodData(year, month)
      setAppointments(apptData)
      setRoutines(routineData)
    } catch {
      // silently fail
    } finally {
      setLoading(false)
    }
  }, [year, month])

  useEffect(() => {
    let cancelled = false
    loadPeriodData(year, month)
      .then(([apptData, routineData]) => {
        if (cancelled) return
        setAppointments(apptData)
        setRoutines(routineData)
      })
      .catch(() => {
        // silently fail
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [year, month])

  function navigate(dir: -1 | 1) {
    setCurrentDate((prev) => {
      const d = new Date(prev)
      if (viewMode === "month") {
        d.setMonth(d.getMonth() + dir)
      } else {
        d.setDate(d.getDate() + dir * 7)
      }
      return d
    })
  }

  function goToday() {
    setCurrentDate(new Date())
  }

  // Format datetime as local ISO string without UTC conversion
  function localDateTimeStr(d: Date): string {
    const date = localDateStr(d)
    const h = String(d.getHours()).padStart(2, "0")
    const m = String(d.getMinutes()).padStart(2, "0")
    return `${date}T${h}:${m}:00`
  }

  // Drag & drop: move appointment to a new date (month view)
  async function handleDropMonth(appointmentId: number, targetDate: Date) {
    const appt = appointments.find((a) => a.id === appointmentId)
    if (!appt) return
    const oldStart = new Date(appt.start_time)
    const oldEnd = new Date(appt.end_time)
    // Keep same time, change date
    const newStart = new Date(targetDate)
    newStart.setHours(oldStart.getHours(), oldStart.getMinutes(), 0)
    const newEnd = new Date(targetDate)
    newEnd.setHours(oldEnd.getHours(), oldEnd.getMinutes(), 0)
    try {
      const updated = await updateAppointment(appointmentId, {
        start_time: localDateTimeStr(newStart),
        end_time: localDateTimeStr(newEnd),
      })
      setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
    } catch {
      // silently fail
    }
  }

  // Drag & drop: move appointment to a new date+hour (week view)
  async function handleDropWeek(appointmentId: number, targetDate: Date, hour: number) {
    const appt = appointments.find((a) => a.id === appointmentId)
    if (!appt) return
    const oldStart = new Date(appt.start_time)
    const oldEnd = new Date(appt.end_time)
    const durationMs = oldEnd.getTime() - oldStart.getTime()
    const newStart = new Date(targetDate)
    newStart.setHours(hour, 0, 0)
    const newEnd = new Date(newStart.getTime() + durationMs)
    try {
      const updated = await updateAppointment(appointmentId, {
        start_time: localDateTimeStr(newStart),
        end_time: localDateTimeStr(newEnd),
      })
      setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
    } catch {
      // silently fail
    }
  }

  // Counts
  const scheduled = appointments.filter((a) => a.status === "scheduled").length
  const personal = appointments.filter(
    (a) => a.appointment_type === "personal" && a.status === "scheduled",
  ).length
  const group = appointments.filter(
    (a) => a.appointment_type === "group_class" && a.status === "scheduled",
  ).length

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
            <Calendar className="text-primary" size={20} />
          </div>
          <div>
            <h2
              className="text-xl font-bold tracking-wide text-white"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Agenda de Citas
            </h2>
            <p className="text-xs text-muted">
              {scheduled} agendadas — {personal} personales, {group} grupales
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition"
        >
          <Plus size={16} />
          Nueva Cita
        </button>
      </div>

      {/* Navigation bar */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg border border-white/10 p-2 text-muted hover:text-white hover:border-white/20 transition"
          >
            <ChevronLeft size={16} />
          </button>
          <h3 className="min-w-[180px] text-center text-sm font-semibold text-white">
            {viewMode === "month"
              ? `${MONTH_NAMES[month]} ${year}`
              : (() => {
                  const d = new Date(currentDate)
                  const day = d.getDay()
                  const diff = day === 0 ? -6 : 1 - day
                  d.setDate(d.getDate() + diff)
                  const end = new Date(d)
                  end.setDate(d.getDate() + 6)
                  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)} — ${end.getDate()} ${MONTH_NAMES[end.getMonth()].slice(0, 3)} ${end.getFullYear()}`
                })()}
          </h3>
          <button
            onClick={() => navigate(1)}
            className="rounded-lg border border-white/10 p-2 text-muted hover:text-white hover:border-white/20 transition"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={goToday}
            className="ml-2 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted hover:text-white hover:border-white/20 transition"
          >
            Hoy
          </button>
        </div>

        {/* View toggle */}
        <div className="flex rounded-lg border border-white/10 overflow-hidden">
          <button
            onClick={() => setViewMode("month")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
              viewMode === "month" ? "bg-primary/10 text-primary" : "text-muted hover:text-white"
            }`}
          >
            <CalendarDays size={14} />
            Mes
          </button>
          <button
            onClick={() => setViewMode("week")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
              viewMode === "week" ? "bg-primary/10 text-primary" : "text-muted hover:text-white"
            }`}
          >
            <CalendarRange size={14} />
            Semana
          </button>
        </div>
      </div>

      {/* Series result banner */}
      {seriesMessage && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-2.5 text-xs text-primary">
          <span>{seriesMessage}</span>
          <button
            onClick={() => setSeriesMessage(null)}
            className="shrink-0 text-primary/70 hover:text-primary transition"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Calendar */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : viewMode === "month" ? (
        <MonthView
          year={year}
          month={month}
          appointments={appointments}
          routines={routines}
          onSelectAppointment={setEditing}
          onDropAppointment={handleDropMonth}
        />
      ) : (
        <WeekView
          currentDate={currentDate}
          appointments={appointments}
          routines={routines}
          onSelectAppointment={setEditing}
          onDropAppointment={handleDropWeek}
        />
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4">
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="h-2.5 w-2.5 rounded-full bg-primary" /> Personal
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Grupal
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="h-2.5 w-2.5 rounded-full bg-violet-400" /> Rutina
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> Completada
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" /> Cancelada
        </span>
      </div>

      {/* Modals */}
      {showAdd && (
        <AddAppointmentModal
          onClose={() => setShowAdd(false)}
          onCreated={(a) => {
            setAppointments((prev) => [...prev, a])
            setShowAdd(false)
          }}
          onCreatedMany={(skipped) => {
            fetchAppointments()
            setShowAdd(false)
            setSeriesMessage(
              skipped.length > 0
                ? `Serie creada. ${skipped.length} fecha(s) omitida(s) por falta de cupo: ${skipped.join(", ")}`
                : "Serie de citas creada correctamente.",
            )
          }}
          defaultDate={currentDate}
        />
      )}

      {editing && (
        <EditAppointmentModal
          appointment={editing}
          onClose={() => setEditing(null)}
          onUpdated={(updated) => {
            setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
            setEditing(null)
          }}
          onDeleted={(id) => {
            setAppointments((prev) => prev.filter((a) => a.id !== id))
            setEditing(null)
          }}
          onDeletedSeries={() => {
            fetchAppointments()
            setEditing(null)
          }}
        />
      )}
    </motion.div>
  )
}
